'use client';

import { langHome, langPrefix } from '@/lib/locale-path';
import React from 'react';
import Link from 'next/link';
// The listing wizard is prefetched on intent (components/IntentLink.tsx).
import IntentLink from '@/components/IntentLink';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Home, Search, Heart, Scale, Plus } from 'lucide-react';
import { listingsPath } from '@/lib/results-paging';
import { notify } from '@/lib/dialogs';
import { getFavoriteIds, useFavoriteIds, useCompareIds } from '@/lib/local-lists';
import { isComparePath, comparePath } from '@/lib/localized-paths';

interface MobileBottomNavProps {
  lang: string;
}

export default function MobileBottomNav({ lang }: MobileBottomNavProps) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isFr = lang === 'fr';

  // Straight from the store: no mount effect, no listener, no cleanup.
  const favCount = useFavoriteIds().length;
  const compCount = useCompareIds().length;

  const isHome = pathname === `${langHome(lang)}` || pathname === `${langPrefix(lang)}/`;
  const isCompare = isComparePath(pathname);
  const isFavorites = searchParams?.get('favorites') === 'true';

  const handleFavoritesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (favCount === 0) {
      notify(isFr ? "Vous n'avez pas encore de favoris." : 'You do not have any favorites yet.', 'info');
      return;
    }
    const favArray = getFavoriteIds();
    const params = new URLSearchParams(searchParams?.toString() || '');
    if (isFavorites) {
      params.delete('favorites');
      params.delete('ids');
      router.push(`${listingsPath(lang)}?${params.toString()}`);
    } else {
      params.set('favorites', 'true');
      params.set('ids', favArray.join(','));
      router.push(`${listingsPath(lang)}?${params.toString()}`);
    }
  };

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isHome) {
      router.push(`${langHome(lang)}`);
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
    if (searchInput) {
      setTimeout(() => searchInput.focus(), 300);
    }
  };

  // The agency landing page has its own bar with its own call to action over the same strip of the screen
  // (it hid the tab bar but the raised Sell button showed above it).
  if (pathname && /^(?:\/(?:en|fr))?\/partner\/?$/.test(pathname)) return null;

  return (
    <>
      <nav
        id="mobile-bottom-nav"
        aria-label={isFr ? 'Navigation mobile' : 'Mobile navigation'}
        // The bar used to be 68px with 6px of padding, which left the raised
        // button nowhere to go: its circle almost touched the top border, and
        // because the button lifted while its label stayed put, "List" sat on a
        // different baseline from the other four. Taller bar, bigger circle,
        // and a ring in the page colour so the bar reads as notched around it.
        //
        // An app's tab bar, so no browser link menu: holding a finger on Sell
        // in Chrome on Android opened "Open in new tab / Copy link address...".
        // A tap still navigates; a long press now does nothing, selects no
        // text and (iOS) shows no link preview.
        //
        // No top border and no upward shadow: the shadow blurred 20px above the
        // bar and read as a second, taller gradient. The 8px fade is the edge
        // now (the owner
        // asked for the thin line to go).
        onContextMenu={(e) => e.preventDefault()}
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bar-surface px-2 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition select-none [-webkit-touch-callout:none]"
      >
        {/* 8px above the bar (24, then 12, then 8, then 6; the owner settled on 8 on 2026-09-24), fading
            from the bar's colour to nothing: the page slides under the bar
            instead of being cut by its top edge, and the raised Sell button
            reads as part of the bar. It takes no taps - whatever is under it
            still gets them. */}
        <span aria-hidden="true" className="bar-fade-up" />
        <div className="flex items-end justify-around max-w-lg mx-auto">
          {/* Home */}
          <Link
            href={`${langHome(lang)}`}
            className={`flex flex-col items-center justify-center min-w-[60px] py-1.5 px-1 rounded-xl transition-colors cursor-pointer ${
              isHome && !isFavorites
                ? 'text-white'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            <Home className="w-[22px] h-[22px] mb-1" />
            <span className="text-xs font-semibold tracking-tight leading-none">
              {isFr ? 'Accueil' : 'Home'}
            </span>
          </Link>

          {/* Search */}
          <button
            type="button"
            onClick={handleSearchClick}
            className="flex flex-col items-center justify-center min-w-[60px] py-1.5 px-1 rounded-xl text-neutral-400 hover:text-white transition-colors cursor-pointer"
          >
            <Search className="w-[22px] h-[22px] mb-1" />
            <span className="text-xs font-semibold tracking-tight leading-none">
              {isFr ? 'Recherche' : 'Search'}
            </span>
          </button>

          {/* Add Listing Center Button: straight to the private-seller wizard,
              same as the top navbar's Add Listing button.

              Five things make this read as deliberate rather than merely
              bigger:

              1. The ring is the page colour rather than a border, so the bar
                 appears notched around the button instead of having a disc
                 dropped on top of its top edge.
              2. The ring is fully opaque while the bar behind it is 95% with a
                 blur. At the seam that difference is visible, so the ring gets
                 a matching translucency and the notch edge stays clean over
                 whatever is scrolling underneath.
              3. The shadow is two parts: a tight dark one that seats the
                 button on the bar, and a wide faint gold one that reads as
                 warmth rather than a glow. A single coloured shadow at
                 shadow-md looked like a halo sticker.
              4. On press it drops to 0.94 and the gold shadow goes, which is
                 how a physical button behaves - it stops floating.
              5. The label is neutral, not gold. With a gold circle directly
                 above it, gold text competed with it; neutral text lets the
                 circle be the only gold thing in the bar and it carries much
                 further. */}
          {/* 2026-09-22: "List" read as "a list", in small grey type under the
              most important button in the bar. The label now says what the
              button does (sell; the wizard only takes properties for sale),
              in the bar's strongest type, on the same baseline as the other
              four. The circle is a champagne-to-gold gradient with a light
              top edge, with a faint warm light breathing beneath it (none for
              anyone who has asked the system to reduce motion). Its label is
              the bar's only gold word. The whole column is the target, gives
              on press, and taps back on phones that can vibrate. */}
          <IntentLink
            href={`${langPrefix(lang)}/add?type=private`}
            className="group flex flex-col items-center justify-end -mt-[30px] min-w-[64px] px-2 pt-1 pb-1.5 cursor-pointer select-none"
            aria-label={isFr ? 'Vendre votre propriété' : 'Sell your property'}
            // A short tap under the finger on phones that support it
            // (Android; iOS Safari has no vibration API and simply ignores it).
            onPointerDown={() => {
              if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(12);
            }}
          >
            <span className="relative flex transition-transform duration-150 ease-out group-active:scale-[0.94]">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-[3px] left-[10px] right-[10px] h-[22px] rounded-full bg-champagne/60 blur-[10px] animate-sell-glow motion-reduce:animate-none"
              />
              <span
                className="relative w-[58px] h-[58px] rounded-full border-[3.5px] border-neutral-950/95 bg-gradient-to-b from-champagne to-gold text-neutral-900 flex items-center justify-center transition-shadow duration-150 shadow-[inset_0_1px_0_rgba(255,255,255,0.65),0_2px_6px_rgba(0,0,0,0.28),0_8px_24px_-6px_color-mix(in_oklab,var(--color-gold)_60%,transparent)] group-active:shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_1px_3px_rgba(0,0,0,0.3)] group-focus-visible:ring-2 group-focus-visible:ring-gold group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-neutral-950"
              >
                <Plus className="w-7 h-7" strokeWidth={2.25} />
              </span>
            </span>
            <span className="mt-1.5 text-[12px] font-bold tracking-tight leading-none text-champagne transition-transform duration-150 group-active:scale-[0.96]">
              {isFr ? 'Vendre' : 'Sell'}
            </span>
          </IntentLink>

          {/* Favorites */}
          <button
            type="button"
            onClick={handleFavoritesClick}
            className={`relative flex flex-col items-center justify-center min-w-[60px] py-1.5 px-1 rounded-xl transition-colors cursor-pointer ${
              isFavorites
                ? 'text-danger'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            <div className="relative">
              <Heart className={`w-[22px] h-[22px] mb-1 ${isFavorites ? 'fill-current text-danger' : ''}`} />
              {favCount > 0 && (
                <span className="absolute -top-1 -right-2 bg-danger text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {favCount > 9 ? '9+' : favCount}
                </span>
              )}
            </div>
            <span className="text-xs font-semibold tracking-tight leading-none">
              {isFr ? 'Favoris' : 'Favorites'}
            </span>
          </button>

          {/* Compare */}
          <Link
            href={comparePath(lang)}
            className={`relative flex flex-col items-center justify-center min-w-[60px] py-1.5 px-1 rounded-xl transition-colors cursor-pointer ${
              isCompare
                ? 'text-champagne'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            {/* How many listings are picked (owner's approval, 2026-09-28). */}
            <div className="relative">
              <Scale className="w-[22px] h-[22px] mb-1" />
              {compCount > 0 && (
                <span aria-label={isFr ? `${compCount} à comparer` : `${compCount} to compare`} className="absolute -top-1 -right-2 bg-gold text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {compCount}
                </span>
              )}
            </div>
            <span className="text-xs font-semibold tracking-tight leading-none">
              {isFr ? 'Comparer' : 'Compare'}
            </span>
          </Link>
        </div>
      </nav>
    </>
  );
}
